/**
 * ModalShell - Reusable modal wrapper for the resource collection flow
 */

import React, { useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './ModalShell.css';

interface ModalShellProps {
  title: string;
  children: React.ReactNode;
  onClose?: () => void;
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  backLabel?: string;
  nextDisabled?: boolean;
  showClose?: boolean;
  showFooter?: boolean;
  className?: string;
  wide?: boolean;
}

const ModalShell: React.FC<ModalShellProps> = ({
  title,
  children,
  onClose,
  onBack,
  onNext,
  nextLabel,
  backLabel,
  nextDisabled = false,
  showClose = true,
  showFooter = true,
  className = '',
  wide = false,
}) => {
  const { t } = useTranslation(['creator', 'common']);
  const modalRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && onClose) {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (onClose) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [handleKeyDown, onClose]);

  // Focus trap
  useEffect(() => {
    modalRef.current?.focus();
  }, []);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  return (
    <div className="resource-modal-backdrop" onClick={handleBackdropClick}>
      <div
        className={`resource-modal-shell ${wide ? 'resource-modal-wide' : ''} ${className}`}
        ref={modalRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="resource-modal-header">
          <h2>{title}</h2>
          {showClose && onClose && (
            <button className="resource-modal-close" onClick={onClose} aria-label={t('common:close')}>
              &times;
            </button>
          )}
        </header>

        <div className="resource-modal-body">
          {children}
        </div>

        {showFooter && (
          <footer className="resource-modal-footer">
            {onBack ? (
              <button className="btn-modal-back" onClick={onBack}>
                {backLabel || t('common:back')}
              </button>
            ) : <div />}
            {onNext && (
              <button className="btn-modal-next" onClick={onNext} disabled={nextDisabled}>
                {nextLabel || t('common:next')}
              </button>
            )}
          </footer>
        )}
      </div>
    </div>
  );
};

export default ModalShell;
